import React, { useState, useEffect, useRef } from 'react'
import { MaterialColor } from '../../domain/enuns/MaterialColor'
import { AnexoFile, Props, Serie, } from '../../domain/entities/MaterialForm'
import { FaCheck, FaLongArrowAltDown, FaRegTrashAlt } from "react-icons/fa";
import { FiUploadCloud } from "react-icons/fi";

import * as S from './style'

const UrlBase = process.env.REACT_APP_API

const formats = ['pdf', 'pptx', 'jpeg', 'jpg', 'png', 'docx', 'xlsx']
const colors: { key: keyof typeof MaterialColor; value: string }[] = Object.entries(MaterialColor).map(([key, value]) => ({ key: key as keyof typeof MaterialColor, value }))

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const MaterialForm: React.FC<Props> = ({
  material,
  niveisEnsino,
  tipos,
  anos,
  onCancel,
  onSave,
  onDownload,
  onError,
}) => {
  const isEditing = Boolean(material)

  const [titulo, setTitulo] = useState(material?.tituloMaterial ?? '')
  const [ano, setAno] = useState<string>(material?.ano ? String(material.ano) : '')
  const [nivelEnsinoId, setNivelEnsinoId] = useState<string>(material?.nivelEnsinoId ? String(material.nivelEnsinoId) : '')
  const [serieId, setSerieId] = useState<string>(material?.serieId ? String(material.serieId) : '')
  const [tiposSelected, setTiposSelected] = useState<number[]>(material?.tipoId ? [material.tipoId] : [])
  const [cor, setCor] = useState<string>(material?.cor ?? '')
  const [anexo, setAnexo] = useState<AnexoFile | null>(
    material?.anexo
      ? {
          nome: material.anexo,
          tipo: material.anexo.split('.').pop()?.toUpperCase() ?? '',
          tamanho: '',
          url: `${UrlBase}/Portal/materiaisDidaticos/download/${material.anexo}`,
          isExisting: true,
        }
      : null
  )
  const [file, setFile] = useState<File | null>(null)

  const [series, setSeries] = useState<Serie[]>([])
  const [dragging, setDragging] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchSeries = async (nivelId: number) => {
    const result = await fetch(`${UrlBase}/Portal/series?nivelEnsino=${nivelId}`)
    const data = await result.json()
    setSeries(data)
  }

  // Preenche campos do formulário com os dados de criação/edição
  useEffect(() => {
    setTitulo(material?.tituloMaterial ?? '')
    setAno(material?.ano ? String(material.ano) : '')
    setNivelEnsinoId(material?.nivelEnsinoId ? String(material.nivelEnsinoId) : '')
    setSerieId(material?.serieId ? String(material.serieId) : '')
    setTiposSelected(material?.tipoId ? [material.tipoId] : [])
    setCor(material?.cor ?? '')
    setAnexo(
      material?.anexo
        ? {
            nome: material.anexo,
            tipo: material.anexo.split('.').pop()?.toUpperCase() ?? '',
            tamanho: '',
            url: `${UrlBase}/Portal/materiaisDidaticos/download/${material.anexo}`,
            isExisting: true,
          }
        : null,
    )
    setFile(null)
    setErrors({})
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [material])

  useEffect(() => {
    if (!nivelEnsinoId) {
      setSeries([])
      setSerieId('')
      return
    }
    fetchSeries(Number(nivelEnsinoId)).catch(console.error)
  }, [nivelEnsinoId])


  const handleFile = (file: File) => {
    const extensao = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!formats.includes(extensao)) {
      setErrors(e => ({ ...e,
        anexo: `Formato inválido. Permitidos: PDF, PPTX, JPEG, JPG, PNG, DOCX e XLSX.`,
      }))
      return
    }
    if (file.size > 50 * 1024 * 1024) {
      setErrors(e => ({ ...e, anexo: `O arquivo excede o tamanho máximo de 50 MB.` }))
      return
    }
    setFile(file)
    setAnexo({
      nome: file.name,
      tipo: extensao.toUpperCase(),
      tamanho: formatFileSize(file.size),
      url: URL.createObjectURL(file),
      isExisting: false,
    })
    setErrors(e => ({ ...e, anexo: '' }))
  }
 
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFile(file)
    }
  }
 
  const handleRemoveAnexo = () => {
    setAnexo(null)
    setFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setErrors(e => ({ ...e, anexo: 'Anexo obrigatório.' }))
  }

  const handleTipoToggle = (id: number) => {
    setTiposSelected(prev => prev.includes(id) ? [] : [id])
  }

  const validarForm = () => {
    const errors: Record<string, string> = {}
    if (!titulo.trim()) {
      errors.titulo = "É necessário informar o título do material."
    }

    if (!ano.trim()) {
      errors.ano = "É necessário informar o ano do material."
    } 

    if (!/^\d{4}$/.test(ano)) {
      errors.ano = "Ano deve conter 4 números."
    }

    if (!cor) {
      errors.cor = "É necessário selecionar uma cor para o material."
    }

    if (!anexo) {
      errors.anexo = "É necessário anexar um arquivo ao material."
    }
    return errors
  }

  // Salvar arquivo editado ou criado e fazer upload do anexo
  const handleSave = async () => {
    const errors = validarForm()

    if (Object.keys(errors).length) {
      setErrors(errors)
      return
    }

    setSaving(true)

    try {
      let anexoNome = anexo?.nome ?? ''
      if (file) {
        const formData = new FormData()
        formData.append('file', file)

        const upload = await fetch(`${UrlBase}/Portal/materiaisDidaticos/upload`, {
          method: 'POST',
          body: formData,
        })

        if (!upload.ok) {
          const txt = await upload.text()
          const message = txt.replace(/^"|"$/g, '').trim()
          setErrors(e => ({
            ...e,
            anexo: message || 'Erro ao enviar o arquivo. Verifique o formato e tente novamente.',
          }))
          setSaving(false)
          return
        }

        const rawNome = await upload.text()
        try {
          anexoNome = JSON.parse(rawNome)
        } catch {
          anexoNome = rawNome.replace(/^"|"$/g, '').trim()
        }
      }

      const params = new URLSearchParams()

      if (material?.id) {
        params.append('id', String(material.id))
      }
      params.append('titulo', titulo.trim())
      params.append('ano', ano)
      params.append('cor', cor)
      if (serieId) {
        params.append('serie', serieId)
      }
      if (nivelEnsinoId) {
        params.append('nivelEnsino', nivelEnsinoId)
      }
      if (tiposSelected[0]) {
        params.append('tipo', String(tiposSelected[0]))
      }
      params.append('anexo', anexoNome)

      const save = await fetch(`${UrlBase}/Portal/materiaisDidaticos?${params.toString()}`, {
        method: 'POST',
      })

      if (!save.ok) {
        const message = await save.text()
        onError(message || 'Não foi possível salvar o material. Tente novamente.')
        setSaving(false)
        return
      }

      onSave(isEditing)
    } catch (error) {
      console.error(error)
      onError('Erro inesperado ao salvar. Tente novamente.')
      setSaving(false)
    }
  }
  

  return (
    <S.PageWrapper>

      <S.FormCard>
        <S.FormTitle> {isEditing ? 'Editar Material' : 'Novo Material'}</S.FormTitle>
        <S.FormSubtitle> {isEditing ? 'Altere as informações do material didático abaixo.' : 'Preencha as informações do novo material didático.'}</S.FormSubtitle>

        <S.FormGroup>
          <S.FormLabel>
            Título do Material <span style={{ color: '#dc2626' }}>*</span>
          </S.FormLabel>
          <S.FormInput
            maxLength={200}
            value={titulo.trimStart()}
            onChange={(e: { target: { value: React.SetStateAction<string> } }) => setTitulo(e.target.value)}
            onBlur={(e: { target: { value: string } }) => {
              const titulo = e.target.value.replace(/\s+/g, ' ').trim();
              setTitulo(titulo);
            }}
            placeholder="Digite o título do material"
            $hasError={Boolean(errors.titulo)}
          />
          {errors.titulo && <S.ErrorMsg>{errors.titulo}</S.ErrorMsg>}
        </S.FormGroup>

        <S.FormRow>
          <S.FormGroup>
            <S.FormLabel>
              Ano <span style={{ color: '#dc2626' }}>*</span>
            </S.FormLabel>
            <S.FormInput
              value={ano}
              onChange={(e: { target: { value: React.SetStateAction<string> } }) =>
                setAno(String(e.target.value).replace(/\D/g, '').slice(0, 4))
              }
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="Ex: 2026"
              $hasError={Boolean(errors.ano)}
            />
            {errors.ano && <S.ErrorMsg>{errors.ano}</S.ErrorMsg>}
          </S.FormGroup>

          <S.FormGroup>
            <S.FormLabel>Nível de Ensino</S.FormLabel>
            <S.Select
              value={nivelEnsinoId}
              onChange={(e: { target: { value: React.SetStateAction<string> } }) => {
                setNivelEnsinoId(e.target.value)
                setSerieId('')
              }}
            >
              <option value="">Selecione</option>
              {niveisEnsino.map(n => (
                <option key={n.id} value={n.id}>{n.descricao}</option>
              ))}
            </S.Select>
          </S.FormGroup>

          <S.FormGroup>
            <S.FormLabel>Série</S.FormLabel>
            <S.Select
              value={serieId}
              onChange={(e: { target: { value: React.SetStateAction<string> } }) => setSerieId(e.target.value)}
              disabled={!nivelEnsinoId}
            >
              <option value="">Selecione</option>
              {series.map(s => (
                <option key={s.id} value={s.id}>{s.descricao}</option>
              ))}
            </S.Select>
          </S.FormGroup>
        </S.FormRow>

        <S.FormGroup>
          <S.FormLabel>Tipo de Material</S.FormLabel>
          <S.CheckboxRow>
            {tipos.map(t => (
              <S.CheckboxLabel key={t.id}>
                <S.StyledCheckbox
                  type="checkbox"
                  checked={tiposSelected.includes(t.id)}
                  onChange={() => handleTipoToggle(t.id)}
                />
                {t.titulo}
              </S.CheckboxLabel>
            ))}
          </S.CheckboxRow>
        </S.FormGroup>

        <S.FormGroup>
          <S.FormLabel>
            Cor do Card <span style={{ color: '#dc2626' }}>*</span>
          </S.FormLabel>
          <S.CheckboxRow style={{ gap: '12px' }}>
            {colors.map(opt => (
              <S.ColorSwatch
                key={opt.key}
                $color={opt.value}
                $selected={cor === opt.value}
                onClick={() => setCor(opt.value)}
              >
                {cor === opt.value && (
                  <FaCheck color="white" />
                )}
              </S.ColorSwatch>
            ))}
          </S.CheckboxRow>
          {errors.cor && <S.ErrorMsg>{errors.cor}</S.ErrorMsg>}
        </S.FormGroup>

        <S.FormGroup>
          <S.FormLabel>
            Anexo do Material <span style={{ color: '#dc2626' }}>*</span>
          </S.FormLabel>

          <S.DropContainer
            $dragging={dragging}
            $hasError={Boolean(errors.anexo)}
            onDragOver={(e: { preventDefault: () => void }) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            {anexo && (
              <S.AnexoItem>
                <S.AnexoIcon>{anexo.tipo}</S.AnexoIcon>
                <S.AnexoInfo>
                  <span>{anexo.nome}</span>
                  <small>{anexo.tipo}{anexo.tamanho ? ` · ${anexo.tamanho}` : ''}</small>
                </S.AnexoInfo>
                <S.ActionsCell>
                  <S.AnexoBtn
                    onClick={() => {
                      const link = document.createElement('a')
                      link.href = anexo.url
                      link.download = anexo.nome
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }}
                  >
                    <FaLongArrowAltDown size={10} />Baixar
                  </S.AnexoBtn>
                  <S.AnexoDeleteBtn onClick={handleRemoveAnexo} title="Remover">
                    <FaRegTrashAlt color="tomato" />
                  </S.AnexoDeleteBtn>
                </S.ActionsCell>
              </S.AnexoItem>
            )}

            <S.DropContainerBody>
              <FiUploadCloud color="#16a34a" size={55} />
              <p>Arraste e solte o arquivo aqui</p>
              <span>ou</span>
              <S.SelectFileBtn onClick={() => fileInputRef.current?.click()}>Selecionar arquivo</S.SelectFileBtn>
              <input
                ref={fileInputRef}
                type="file"
                accept={formats.map(ext => `.${ext}`).join(',')}
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleFile(file)
                  }
                }}
              />
              <S.DropHint>
                Formatos permitidos: PDF, PPTX, JPEG, JPG, PNG, DOCX e XLSX.
                <br />
                Tamanho máximo do arquivo: <strong>50 MB</strong>.
              </S.DropHint>
            </S.DropContainerBody>
          </S.DropContainer>
          {errors.anexo && <S.ErrorMsg>{errors.anexo}</S.ErrorMsg>}
        </S.FormGroup>

        <S.FormBtns>
          <S.CancelBtn onClick={onCancel} disabled={saving}>Cancelar</S.CancelBtn>
          <S.SaveBtn onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</S.SaveBtn>
        </S.FormBtns>
      </S.FormCard>
    </S.PageWrapper>
  )
}

export default MaterialForm